'use client';

import { formatReadableNumber } from '@lfg/core/lib';
import { ArrowForward } from '@mui/icons-material';
import { Box, CircularProgress, Grid2, IconButton, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';

import { icons } from '@/assets/icons';
import { dividers, highlights } from '@/assets/materials';
import type { NodeSchemaType } from '@/global/schemas/node.schema';
import { useFetchNodesQuery } from '@/store/services/node.service';

import Button from './bases/Button';
import { CoinIcon } from './bases/CoinIcon';
import { FilterBar } from './bases/FilterBar';
import { NodeCard } from './bases/NodeCard';
import NodeDetailModal, { type NodeDetailModalHandle } from './bases/NodeDetailModal';
import { Pagination } from './bases/Pagination';

export const ExploreLiveNodeSection = () => {
  const theme = useTheme();
  const modalRef = useRef<NodeDetailModalHandle>(null);
  const [page, setPage] = useState<number>(1);

  const [selectedNode, setSelectedNode] = useState<NodeSchemaType | null>(null);

  const [searchValue, setSearchValue] = useState<string>('');
  const [sortValue, setSortValue] = useState<string>('{"price": "desc"}');
  const [networkValues, setNetworkValues] = useState<string[]>(['cm92ajbbq0000qf0006mqakd6']); // hardcode for default ethereum network

  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  const {
    data: nodes,
    isLoading: isLoadingNodes,
    isFetching: isFetchingNodes,
  } = useFetchNodesQuery({
    page,
    search: debouncedSearch || undefined,
    sort: sortValue,
    filter: JSON.stringify({
      networkId: networkValues.length > 0 ? { in: networkValues } : undefined,
    }),
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sortValue, networkValues]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);

    // Scroll to top of live nodes section
    const element = document.getElementById('explore-live-node');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  const openNodeSaleUrl = (url: string) => {
    window.open(url, '_blank');
  };

  const handleOpenNodeDetail = (item: NodeSchemaType) => {
    setSelectedNode(item);
    setTimeout(() => modalRef.current?.show(), 300);
  };

  return (
    <Stack
      id={'explore-live-node'}
      width={'100%'}
      height={'100%'}
      flexGrow={1}
      minHeight={'852px'}
      padding={{ xs: '32px 12px', md: '80px 80px 0 80px' }}
      marginY={{ xs: '12px', md: '30px' }}
      alignItems={'center'}
      component={'section'}
      position={'relative'}>
      {selectedNode !== null && (
        <NodeDetailModal ref={modalRef} item={selectedNode} setSelectedNode={setSelectedNode} />
      )}
      <Stack gap={{ xs: 2, sm: 7 }} width={'min(1280px, 100%)'} zIndex={2}>
        <Stack
          justifyContent={'space-between'}
          gap={2}
          width={'100%'}
          direction={{
            xs: 'column',
            md: 'row',
          }}
          sx={{
            alignItems: { xs: 'flex-start', md: 'center' },
          }}>
          <Typography
            component={'h2'}
            variant={'displayMedium'}
            className={'clip-text'}
            fontSize={{ xs: '24px', md: '48px' }}
            lineHeight={{ xs: '32px', md: '68px' }}
            sx={{ background: 'linear-gradient(90deg, #A9A9A9 0%, #FFFFFF 50%, #A9A9A9 100%)', whiteSpace: 'nowrap' }}>
            Live Collections
          </Typography>

          {/* Filter Bar */}
          <FilterBar
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            sortValue={sortValue}
            onSortChange={setSortValue}
            networkValues={networkValues}
            onNetworkChange={setNetworkValues}
          />
        </Stack>

        {isLoadingNodes ? (
          <Grid2 container width={'100%'} spacing={{ xs: 2, md: 4 }}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Grid2 key={`skeleton-${index}`} size={{ xs: 12, sm: 6, md: 4 }}>
                <NodeCard className={'h-full'}>
                  <Stack
                    width={'100%'}
                    gap={3}
                    sx={{ height: '100%', border: 1, borderColor: '#ffffff1a', borderRadius: '24px' }}>
                    <Stack width={'100%'} height={'248px'} overflow={'hidden'} sx={{ borderRadius: '24px 24px 0 0' }}>
                      <Skeleton
                        variant='rectangular'
                        width='100%'
                        height='100%'
                        sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                      />
                    </Stack>

                    {/* Content Skeleton */}
                    <Stack padding={'0 24px'} gap={3} sx={{ flex: 1 }}>
                      {/* Project Info Skeleton */}
                      <Stack gap={2}>
                        <Skeleton
                          variant='text'
                          width='80%'
                          height={48}
                          sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                        />
                        <Stack gap={1} minHeight={'72px'}>
                          <Skeleton
                            variant='text'
                            width='100%'
                            height={24}
                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                          />
                          <Skeleton
                            variant='text'
                            width='90%'
                            height={24}
                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                          />
                          <Skeleton
                            variant='text'
                            width='70%'
                            height={24}
                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                          />
                        </Stack>
                      </Stack>

                      {/* Tier Info Card Skeleton */}
                      <Stack
                        direction={'row'}
                        alignItems={'center'}
                        gap={2}
                        padding={'24px'}
                        sx={{
                          background: theme.palette.whiteAlpha['03'],
                          border: `1px solid ${theme.palette.whiteAlpha['06']}`,
                          borderRadius: '16px',
                        }}>
                        {/* Tier Details Skeleton */}
                        <Stack gap={1.5} flex={1}>
                          <Skeleton
                            variant='text'
                            width='60%'
                            height={20}
                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                          />
                          <Skeleton
                            variant='text'
                            width='40%'
                            height={28}
                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                          />
                        </Stack>

                        {/* Divider Line */}
                        <Box
                          width={'0px'}
                          height={'100%'}
                          sx={{
                            border: `1px solid ${theme.palette.whiteAlpha['06']}`,
                          }}
                        />

                        {/* Price Info Skeleton */}
                        <Stack gap={1.5} flex={1}>
                          <Skeleton
                            variant='text'
                            width='30%'
                            height={20}
                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                          />
                          <Skeleton
                            variant='text'
                            width='70%'
                            height={28}
                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                          />
                        </Stack>

                        {/* Action Button Skeleton */}
                        <Skeleton
                          variant='circular'
                          width={48}
                          height={48}
                          sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                </NodeCard>
              </Grid2>
            ))}
          </Grid2>
        ) : nodes?.data?.length === 0 ? (
          // No nodes found state
          <Stack
            alignItems='center'
            justifyContent='center'
            sx={{
              minHeight: '400px',
              padding: '40px 20px',
              textAlign: 'center',
            }}>
            <Stack alignItems='center' gap={3}>
              <Stack gap={1}>
                <Typography
                  variant='h4'
                  sx={{
                    color: 'white',
                    fontSize: { xs: '20px', sm: '24px' },
                    fontWeight: 600,
                  }}>
                  No collections found
                </Typography>
                <Typography
                  variant='body1'
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: { xs: '14px', sm: '16px' },
                    maxWidth: '400px',
                  }}>
                  {searchValue || networkValues.length > 0
                    ? 'Try adjusting your search or filter criteria to find more collections.'
                    : 'There are currently no live collections available. Check back later for new listings.'}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {isFetchingNodes && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  borderRadius: '8px',
                }}>
                <Stack alignItems='center' gap={2}>
                  <CircularProgress
                    size={40}
                    sx={{
                      color: 'white',
                    }}
                  />
                  <Typography
                    variant='body2'
                    sx={{
                      color: 'white',
                      fontSize: '14px',
                    }}>
                    Loading collections...
                  </Typography>
                </Stack>
              </Box>
            )}
            <Grid2 container width={'100%'} spacing={{ xs: 2, md: 4 }}>
              {nodes?.data?.map((item, index) => (
                <Grid2 key={`${item.symbol}_${index + 1}`} size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                  <NodeCard className={'h-full'} onClick={() => openNodeSaleUrl(item.nodeSaleUrl)}>
                    <Stack
                      width={'100%'}
                      gap={3}
                      sx={{ height: '100%', border: 1, borderColor: '#ffffff1a', borderRadius: '24px' }}>
                      <Stack width={'100%'} height={'248px'} overflow={'hidden'} sx={{ borderRadius: '24px 24px 0 0' }}>
                        <Image
                          priority
                          src={item.asset}
                          alt={item.name}
                          width={432}
                          height={248}
                          className={'w-full h-full object-cover'}
                        />
                      </Stack>

                      <Stack padding={{ xs: '0 16px 16px 16px', md: '0 24px 24px 24px' }} gap={3} sx={{ flex: 1 }}>
                        <Stack gap={2}>
                          <Typography
                            variant={'h4'}
                            color={theme.palette.text.primary}
                            fontSize={{ xs: '24px', md: '32px' }}
                            fontWeight={600}
                            lineHeight={'1.5em'}>
                            {item.name}
                          </Typography>
                          <Typography
                            variant={'body1'}
                            color={theme.palette.text.primary}
                            fontSize={{ xs: '14px', md: '16px' }}
                            fontWeight={400}
                            lineHeight={'1.5em'}
                            minHeight={{ xs: '48px', md: '72px' }}
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}>
                            {item.description}
                          </Typography>
                        </Stack>

                        <Stack
                          direction={'row'}
                          alignItems={'center'}
                          gap={2}
                          padding={{ xs: '16px', md: '24px' }}
                          sx={{
                            background: theme.palette.whiteAlpha['03'],
                            border: `1px solid ${theme.palette.whiteAlpha['06']}`,
                            borderRadius: '16px',
                            backdropFilter: 'blur(15px)',
                          }}>
                          <Stack gap={1.5} flex={1}>
                            <Typography
                              color={theme.palette.whiteAlpha['60']}
                              fontSize={{ xs: '14px', md: '16px' }}
                              fontWeight={400}
                              lineHeight={'1.25em'}>
                              Current Tier
                            </Typography>
                            <Typography
                              color={theme.palette.text.primary}
                              fontSize={{ xs: '18px', md: '24px' }}
                              fontWeight={500}
                              lineHeight={'1.17em'}>
                              TIER {item.currentTier}
                            </Typography>
                          </Stack>

                          <Box
                            width={'0px'}
                            height={'100%'}
                            sx={{
                              border: `1px solid ${theme.palette.whiteAlpha['06']}`,
                            }}
                          />

                          <Stack gap={1.5} flex={1}>
                            <Typography
                              color={theme.palette.whiteAlpha['60']}
                              fontSize={{ xs: '14px', md: '16px' }}
                              fontWeight={400}
                              lineHeight={'1.25em'}>
                              Price
                            </Typography>
                            <Stack direction={'row'} alignItems={'center'} gap={1}>
                              <Typography
                                color={theme.palette.text.primary}
                                fontSize={{ xs: '18px', md: '24px' }}
                                fontWeight={500}
                                lineHeight={'1.17em'}>
                                {formatReadableNumber(item.price, {
                                  minimumFractionDigits: 2,
                                  style: 'decimal',
                                  preventRounding: true,
                                })}
                              </Typography>
                              <Typography
                                color={theme.palette.text.primary}
                                fontSize={{ xs: '14px', md: '16px' }}
                                fontWeight={300}
                                lineHeight={'1.17em'}
                                sx={{ opacity: 0.5 }}>
                                {(item.token || 'USDC').toUpperCase()}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack
                            width={48}
                            height={48}
                            alignItems={'center'}
                            justifyContent={'center'}
                            sx={{
                              background: theme.palette.brand.primary,
                              borderRadius: '100px',
                              cursor: 'pointer',
                            }}>
                            <IconButton size={'large'} onClick={() => openNodeSaleUrl(item.nodeSaleUrl)}>
                              <ArrowForward />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Stack>
                  </NodeCard>
                </Grid2>
              ))}
            </Grid2>
          </Box>
        )}
        {!isLoadingNodes && nodes?.meta && nodes?.meta.total > 0 && (
          <Pagination meta={nodes?.meta} onPageChange={handlePageChange} />
        )}
      </Stack>
    </Stack>
  );
};
